import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, PhoneCall } from "lucide-react";
import { NAV_SERVICES } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];

export default function MenuOverlay({ open, onClose, onBook }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="menu-overlay"
          className="fixed inset-0 z-[100] bg-navy-950"
          initial={{ clipPath: "circle(0% at calc(100% - 40px) 40px)" }}
          animate={{ clipPath: "circle(150% at calc(100% - 40px) 40px)" }}
          exit={{ clipPath: "circle(0% at calc(100% - 40px) 40px)" }}
          transition={{ duration: 0.7, ease: EASE }}
          // A full-viewport clip-path circle() is expensive for the browser to
          // recompute every frame — unlike transform/opacity, it's not
          // GPU-composited by default. will-change hints the browser to
          // promote this to its own layer ahead of time, which is what was
          // most likely behind the perceptible freeze on close (worse right
          // after page load, while the main thread is already busy — see the
          // Home.jsx LazyMount fix for the bigger piece of that). Doesn't
          // change how the animation looks, just how cheaply it runs.
          style={{ willChange: "clip-path" }}
        >
          <div className="spotlight absolute inset-0 pointer-events-none" />
          <div className="relative h-full w-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col">
            <div className="h-16 md:h-20 flex items-center justify-between">
              <span className="font-display font-bold tracking-tight text-lg md:text-xl text-white">
                Favor<span className="text-white/50">.</span>Studio
              </span>
              <button
                onClick={onClose}
                data-testid="menu-close-btn"
                aria-label="Close menu"
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 hover:border-white hover:rotate-90 transition-all duration-300"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center py-8">
              <motion.p
                className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6 md:mb-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
              >
                What we build
              </motion.p>

              <nav className="flex flex-col">
                {NAV_SERVICES.map((item, i) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    data-testid={`menu-link-${i}`}
                    className="group border-t border-white/10 last:border-b py-4 md:py-6 flex items-center justify-between"
                  >
                    <span className="overflow-hidden">
                      <motion.span
                        className="block font-display text-4xl md:text-7xl font-semibold tracking-tight text-white/55 group-hover:text-white transition-colors duration-300"
                        initial={{ y: "110%" }}
                        animate={{ y: 0 }}
                        transition={{ delay: 0.3 + i * 0.08, duration: 0.7, ease: EASE }}
                      >
                        {item.label}
                      </motion.span>
                    </span>
                    <span className="flex items-center gap-3 shrink-0 pl-4">
                      {item.tag && (
                        <span className="hidden sm:inline text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/10 text-white border border-white/25">
                          {item.tag}
                        </span>
                      )}
                      <ArrowUpRight className="w-6 h-6 md:w-9 md:h-9 text-white/35 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                    </span>
                  </a>
                ))}
              </nav>
            </div>

            <motion.div
              className="pb-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <button
                onClick={() => { onClose(); onBook(); }}
                data-testid="menu-book-btn"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-sm bg-white hover:bg-white/90 text-navy-900 font-semibold transition-colors self-start"
              >
                <PhoneCall className="w-4 h-4" /> Book a Live Demo
              </button>
              <p className="text-white/40 text-sm">
                Built for residential HVAC. <span className="text-white">Deployed within 14 days.</span>
              </p>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
